<script setup lang="ts">
import type { DropdownMenuItem, TableColumn } from '@nuxt/ui'
import type { Artifact, Channel, Product, Release } from '@/types'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { computed, h, onUnmounted, ref, resolveComponent, watch } from 'vue'
import ArtifactStatusBadge from '@/components/releases/ArtifactStatusBadge.vue'
import ArtifactUploadForm from '@/components/releases/ArtifactUploadForm.vue'
import ChangelogContent from '@/components/releases/ChangelogContent.vue'
import ReleaseFormFields from '@/components/releases/ReleaseFormFields.vue'
import ReleaseStateBadge from '@/components/releases/ReleaseStateBadge.vue'
import { formatBytes, formatDate } from '@/lib/format'
import { dashboardLayout } from '@/lib/layouts'
import { adminArtifactPath, adminProductPath, adminReleasePath, minimumAdminReleasePath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  release: Release
  channel: Pick<Channel, 'id' | 'name' | 'minimum_supported_version' | 'release_source'>
  product: Pick<Product, 'id' | 'slug' | 'name'>
}>()

const UButton = resolveComponent('UButton')

const channelsTabPath = computed(() => `${adminProductPath(props.product.slug)}?tab=channels`)

// Published releases are immutable: their version, release date, and artifacts
// are frozen. Yanking is the only state change a published release allows.
const isPublished = computed(() => Boolean(props.release.published_at))
const isLive = computed(() => props.release.state === 'published')
// GitHub-published releases own their version and artifacts upstream; the
// dashboard only exposes changelog edits and the publish/yank lifecycle.
const isExternallyManaged = computed(() => props.channel.release_source === 'github_actions')
const isChannelMinimum = computed(
  () => props.channel.minimum_supported_version === props.release.version,
)
// The server refuses to publish until every artifact has passed verification.
// Mirror that rule so the action never offers a click the backend will reject.
const allArtifactsReady = computed(() =>
  props.release.artifacts.every(artifact => artifact.status === 'ready'),
)
const signatureBadge = computed(() => {
  const map = {
    valid: { color: 'success' as const, icon: 'i-lucide-shield-check', label: 'Valid signature' },
    invalid: { color: 'error' as const, icon: 'i-lucide-shield-x', label: 'Invalid signature' },
    stale: { color: 'warning' as const, icon: 'i-lucide-shield-alert', label: 'Stale signature' },
  } as const
  return map[props.release.signature_status.status as keyof typeof map]
    ?? { color: 'neutral' as const, icon: 'i-lucide-shield', label: 'Unsigned' }
})

// --- Publish / yank lifecycle ---------------------------------------------
const updating = ref(false)

function patchRelease(data: Record<string, boolean>) {
  router.patch(adminReleasePath(props.release.id), data, {
    preserveScroll: true,
    onStart: () => (updating.value = true),
    onFinish: () => (updating.value = false),
  })
}

function setChannelMinimum() {
  router.patch(minimumAdminReleasePath(props.release.id), {}, {
    preserveScroll: true,
    onStart: () => (updating.value = true),
    onFinish: () => (updating.value = false),
  })
}

// --- Edit release ----------------------------------------------------------
const editOpen = ref(false)
const editForm = useForm({
  version: props.release.version,
  changelog: props.release.changelog ?? '',
})

function openEdit() {
  editForm.defaults({
    version: props.release.version,
    changelog: props.release.changelog ?? '',
  })
  editForm.reset()
  editForm.clearErrors()
  editOpen.value = true
}

function submitEdit() {
  editForm.patch(adminReleasePath(props.release.id), {
    preserveScroll: true,
    onSuccess: () => (editOpen.value = false),
  })
}

// --- Delete release --------------------------------------------------------
const deleteOpen = ref(false)
const deleting = ref(false)

function destroyRelease() {
  router.delete(adminReleasePath(props.release.id), {
    onStart: () => (deleting.value = true),
    onFinish: () => (deleting.value = false),
  })
}

const menuItems = computed<DropdownMenuItem[][]>(() => {
  const lifecycle: DropdownMenuItem[] = []

  // A published release can never be unpublished, so we only offer Publish
  // while it is still a draft and Yank/Un-yank once it is live.
  if (!isPublished.value) {
    lifecycle.push({
      label: 'Publish',
      icon: 'i-lucide-rocket',
      disabled: !allArtifactsReady.value,
      onSelect: () => patchRelease({ published: true }),
    })
  }
  else if (props.release.yanked) {
    lifecycle.push({
      label: 'Un-yank',
      icon: 'i-lucide-undo-2',
      onSelect: () => patchRelease({ yanked: false }),
    })
  }
  else {
    lifecycle.push({
      label: 'Yank',
      icon: 'i-lucide-ban',
      color: 'error',
      onSelect: () => patchRelease({ yanked: true }),
    })
  }

  if (isLive.value && !isChannelMinimum.value) {
    lifecycle.push({
      label: 'Set as channel minimum',
      icon: 'i-lucide-shield-check',
      onSelect: () => setChannelMinimum(),
    })
  }

  const groups: DropdownMenuItem[][] = [lifecycle]

  if (!isPublished.value) {
    const editGroup: DropdownMenuItem[] = [
      {
        label: 'Edit',
        icon: 'i-lucide-pencil',
        onSelect: () => openEdit(),
      },
    ]

    if (!isExternallyManaged.value) {
      editGroup.push({
        label: 'Delete',
        icon: 'i-lucide-trash-2',
        color: 'error',
        onSelect: () => (deleteOpen.value = true),
      })
    }

    groups.push(editGroup)
  }

  return groups
})

// --- Artifacts table -------------------------------------------------------
const columns: TableColumn<Artifact>[] = [
  { accessorKey: 'identifier', header: 'Identifier' },
  { accessorKey: 'filename', header: 'Filename' },
  { accessorKey: 'byte_size', header: 'Size' },
  { accessorKey: 'checksum', header: 'Checksum' },
  { accessorKey: 'status', header: 'Status' },
  { id: 'actions' },
]

// Verification runs asynchronously after upload, so we poll the release prop
// until every artifact has settled into a terminal state.
const POLL_INTERVAL_MS = 3000
const hasVerifyingArtifacts = computed(() =>
  props.release.artifacts.some(artifact => artifact.status === 'verifying'),
)
let pollTimer: ReturnType<typeof setInterval> | undefined

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = undefined
  }
}

watch(hasVerifyingArtifacts, (verifying) => {
  if (verifying && !pollTimer) {
    pollTimer = setInterval(() => {
      router.reload({ only: ['release'] })
    }, POLL_INTERVAL_MS)
  }
  else if (!verifying) {
    stopPolling()
  }
}, { immediate: true })

onUnmounted(stopPolling)

const deleteArtifactTarget = ref<Artifact | null>(null)
const deletingArtifact = ref(false)

function updateDeleteArtifactOpen(value: boolean) {
  if (!value)
    deleteArtifactTarget.value = null
}

function destroyArtifact() {
  if (!deleteArtifactTarget.value)
    return

  router.delete(adminArtifactPath(deleteArtifactTarget.value.id), {
    preserveScroll: true,
    onStart: () => (deletingArtifact.value = true),
    onFinish: () => {
      deletingArtifact.value = false
      deleteArtifactTarget.value = null
    },
  })
}

function rowActions(artifact: Artifact) {
  const actions = [
    h(UButton, {
      'icon': 'i-lucide-download',
      'color': 'neutral',
      'variant': 'ghost',
      'size': 'sm',
      'square': true,
      'aria-label': 'Download',
      'disabled': !artifact.attached,
      'href': artifact.download_path,
      'external': true,
    }),
  ]

  if (!isPublished.value && !isExternallyManaged.value) {
    actions.push(
      h(UButton, {
        'icon': 'i-lucide-trash-2',
        'color': 'neutral',
        'variant': 'ghost',
        'size': 'sm',
        'square': true,
        'aria-label': 'Delete artifact',
        'onClick': () => (deleteArtifactTarget.value = artifact),
      }),
    )
  }

  return h('div', { class: 'flex items-center justify-end gap-1' }, actions)
}
</script>

<template>
  <Head :title="`${product.name} ${release.version}`" />

  <UDashboardPanel id="release-show">
    <template #header>
      <UDashboardNavbar :title="release.version">
        <template #leading>
          <UButton
            :to="channelsTabPath"
            :as="Link"
            icon="i-lucide-arrow-left"
            color="neutral"
            variant="ghost"
            square
          />
        </template>

        <template #right>
          <UButton
            v-if="!release.published_at && !release.yanked"
            label="Publish"
            icon="i-lucide-rocket"
            color="primary"
            variant="solid"
            :loading="updating"
            :disabled="!allArtifactsReady"
            :title="!allArtifactsReady ? 'All artifacts must be verified before publishing' : undefined"
            @click="patchRelease({ published: true })"
          />
          <UDropdownMenu :items="menuItems">
            <UButton icon="i-lucide-ellipsis-vertical" color="neutral" variant="ghost" square />
          </UDropdownMenu>
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <div class="ring-default rounded-lg p-5 ring">
          <div class="mb-4 flex items-center gap-3">
            <Link
              :href="channelsTabPath"
              class="text-muted font-mono text-sm hover:underline"
            >
              {{ product.name }} / {{ channel.name }}
            </Link>
            <ReleaseStateBadge :state="release.state" />
            <UBadge
              v-if="release.supported === false"
              color="error"
              variant="subtle"
              icon="i-lucide-clock-alert"
              label="Unsupported (EOL)"
            />
          </div>

          <dl class="grid gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Version
              </dt>
              <dd class="text-highlighted font-mono">
                {{ release.version }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Published
              </dt>
              <dd class="text-highlighted">
                {{ release.published_at ? formatDate(release.published_at) : 'Not published' }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Channel minimum version
              </dt>
              <dd class="text-highlighted font-mono">
                {{ channel.minimum_supported_version ?? '—' }}
              </dd>
            </div>
          </dl>
        </div>

        <div class="flex flex-col gap-4">
          <div>
            <h2 class="text-highlighted font-semibold">
              Changelog
            </h2>
            <p class="text-muted text-sm">
              Release notes shown to clients in the update manifest and history.
            </p>
          </div>

          <div class="ring-default rounded-lg p-5 ring">
            <ChangelogContent :markdown="release.changelog" />
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div>
            <h2 class="text-highlighted font-semibold">
              Signature
            </h2>
            <p class="text-muted text-sm">
              Server-side verification for the signed release descriptor.
            </p>
          </div>

          <div class="ring-default rounded-lg p-5 ring">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="mb-2 flex items-center gap-2">
                  <UBadge
                    :color="signatureBadge.color"
                    :icon="signatureBadge.icon"
                    :label="signatureBadge.label"
                    variant="subtle"
                  />
                  <span v-if="release.signature_status.key_id" class="text-muted font-mono text-sm">
                    {{ release.signature_status.key_id }}
                  </span>
                </div>
                <p class="text-muted text-sm">
                  {{ release.signature_status.message }}
                </p>
              </div>

              <div v-if="release.signature_status.signed_at" class="text-muted text-right text-sm">
                Signed {{ formatDate(release.signature_status.signed_at) }}
              </div>
            </div>

            <div v-if="release.signature_status.signed" class="mt-5 grid gap-4">
              <div>
                <dt class="text-muted text-xs uppercase tracking-wide">
                  Signature
                </dt>
                <dd class="text-muted mt-1 break-all font-mono text-xs">
                  {{ release.signature_status.signature }}
                </dd>
              </div>

              <details class="group">
                <summary class="text-highlighted flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <UIcon name="i-lucide-file-code" class="size-4" />
                  Signed descriptor
                </summary>
                <pre class="bg-muted/30 text-muted mt-3 max-h-80 overflow-auto rounded-lg p-3 text-xs">{{ release.signature_status.descriptor }}</pre>
              </details>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div>
            <h2 class="text-highlighted font-semibold">
              Artifacts
            </h2>
            <p class="text-muted text-sm">
              Binaries served to clients for this release.
            </p>
          </div>

          <div
            v-if="release.artifacts.length === 0"
            class="ring-default text-muted rounded-lg p-6 text-center text-sm ring"
          >
            No artifacts uploaded yet.
          </div>

          <UTable
            v-else
            :data="release.artifacts"
            :columns="columns"
            class="ring-default rounded-lg ring"
          >
            <template #identifier-cell="{ row }">
              <div class="flex flex-col">
                <span class="text-highlighted font-mono text-sm">{{ row.original.identifier }}</span>
                <span v-if="row.original.description" class="text-muted text-xs">
                  {{ row.original.description }}
                </span>
              </div>
            </template>
            <template #filename-cell="{ row }">
              <span class="text-highlighted font-mono text-sm">{{ row.original.filename }}</span>
            </template>
            <template #byte_size-cell="{ row }">
              <span class="tabular-nums">{{ formatBytes(row.original.byte_size) }}</span>
            </template>
            <template #checksum-cell="{ row }">
              <span class="text-muted font-mono text-xs" :title="row.original.checksum">
                {{ row.original.checksum.slice(0, 12) }}…
              </span>
            </template>
            <template #status-cell="{ row }">
              <div class="flex flex-col gap-1">
                <ArtifactStatusBadge :status="row.original.status" />
                <span
                  v-if="row.original.status === 'failed' && row.original.verification_error"
                  class="text-error text-xs"
                >
                  {{ row.original.verification_error }}
                </span>
              </div>
            </template>
            <template #actions-header>
              <span class="sr-only">Actions</span>
            </template>
            <template #actions-cell="{ row }">
              <component :is="rowActions(row.original)" />
            </template>
          </UTable>

          <div v-if="!isPublished && !isExternallyManaged" class="ring-default rounded-lg p-5 ring">
            <h3 class="text-highlighted mb-4 font-medium">
              Upload artifact
            </h3>
            <ArtifactUploadForm :release="release" />
          </div>
          <p v-else-if="isExternallyManaged" class="text-muted text-sm">
            Artifacts are published by the GitHub trusted workflow and cannot be changed here.
          </p>
          <p v-else class="text-muted text-sm">
            This release is published and its artifacts are locked.
          </p>
        </div>
      </div>
    </template>
  </UDashboardPanel>

  <!-- Edit release modal -->
  <UModal v-model:open="editOpen" title="Edit release">
    <template #body>
      <ReleaseFormFields
        v-model:version="editForm.version"
        v-model:changelog="editForm.changelog"
        :errors="editForm.errors"
        :lock-version="isExternallyManaged"
      />
    </template>
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="editOpen = false" />
        <UButton
          label="Save changes"
          icon="i-lucide-save"
          :loading="editForm.processing"
          @click="submitEdit"
        />
      </div>
    </template>
  </UModal>

  <!-- Delete release confirm -->
  <UModal
    v-model:open="deleteOpen"
    title="Delete release"
    :description="`Delete ${release.version}? This removes its artifacts and cannot be undone.`"
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="deleteOpen = false" />
        <UButton
          label="Delete"
          color="error"
          icon="i-lucide-trash-2"
          :loading="deleting"
          @click="destroyRelease"
        />
      </div>
    </template>
  </UModal>

  <!-- Delete artifact confirm -->
  <UModal
    :open="deleteArtifactTarget !== null"
    title="Delete artifact"
    :description="deleteArtifactTarget ? `Delete ${deleteArtifactTarget.filename}?` : ''"
    @update:open="updateDeleteArtifactOpen"
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="deleteArtifactTarget = null" />
        <UButton
          label="Delete"
          color="error"
          icon="i-lucide-trash-2"
          :loading="deletingArtifact"
          @click="destroyArtifact"
        />
      </div>
    </template>
  </UModal>
</template>
